import { Check, MagnifyingGlass } from '@phosphor-icons/react';
import { KeyboardEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFocusInputFunctionality } from 'core/hooks';
import { useFormContext } from 'core/states';
import { isEnterKeyPress } from 'core/utils';
import { SearchPartBoms } from 'partBoms/components';
import { IGetPartBomsPayload, IPartBom } from 'partBoms/types';
import { Variant } from 'theme/enums';

interface IProps {
  name: string;
  partBomSearchPayload?: IGetPartBomsPayload;
  partBomAutocompletePayload?: IGetPartBomsPayload;
  onChangePartBom: (partBom: IPartBom) => void;
  onKeyDownPartBom?: (event: KeyboardEvent<HTMLInputElement>, value: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  level?: number;
}

export const AdvancedPartBomFormField = ({
  name,
  partBomSearchPayload = {},
  partBomAutocompletePayload = {},
  onChangePartBom,
  onKeyDownPartBom,
  label = undefined,
  placeholder = undefined,
  disabled,
  level = 0,
}: IProps) => {
  const [partBomInputValue, setPartBomInputValue] = useState('');
  const [isPartBomInputFocused, setIsPartBomInputFocused] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const { t } = useTranslation();

  const { control, errors, watch } = useFormContext();

  const { setFocusElementName } = useFocusInputFunctionality();

  const partBomValue = watch(name);

  const onSearchPartBomsButtonClick = (inputValue: string) => {
    setIsPartBomInputFocused(false);
    setPartBomInputValue(inputValue);

    setFocusElementName('searchPartBom');
  };

  const onCloseSearchPartBoms = () => {
    setIsSearchOpen(false);
  };

  const getPartBomCardButtons = (partBom: IPartBom) => {
    return [
      <Button
        key={partBom.id}
        variant={Variant.PLAIN}
        text={t('buttonLabelUsePartBom')}
        onClick={() => {
          onCloseSearchPartBoms();
          onChangePartBom(partBom);
        }}
        startDecorator={<Check weight="bold" />}
      />,
    ];
  };

  return (
    <>
      <FormField
        name={name}
        type={InputType.PART_BOM_AUTOCOMPLETE}
        inputProps={{
          control: control,
          error: errors?.[name],
          label: label === null ? null : label || t('labelPartBomNumber'),
          placeholder: placeholder ?? t('placeholderPartBomNumber'),
          endDecorator: <MagnifyingGlass onClick={() => setIsSearchOpen(true)} />,
          onEndDecoratorClick: onSearchPartBomsButtonClick,
          requestPayload: partBomAutocompletePayload,
          onFocus: () => setIsPartBomInputFocused(true),
          onChange: (event: KeyboardEvent<HTMLInputElement>, option: IPartBom) => {
            if (isEnterKeyPress(event)) {
              event.preventDefault();

              return;
            }

            onChangePartBom(option);
          },
          onKeyDown: isPartBomInputFocused ? onKeyDownPartBom : null,
          disabled,
        }}
      />

      <SearchPartBoms
        isOpen={isSearchOpen}
        onClose={onCloseSearchPartBoms}
        defaultQuery={partBomInputValue || partBomValue?.number}
        partBomSearchPayload={partBomSearchPayload}
        getPartCardButtons={getPartBomCardButtons}
        level={level}
      />
    </>
  );
};
